import * as openaiService from './openaiService';

// ─── CLOUDFLARE R2 CONFIGURATION ─────────────────────────────────────────────
const R2_ACCOUNT_ID    = '4dfe1166a8a61d339d0c0cf02e1ab059';
const R2_BUCKET        = 'recipe-images';
const R2_PUBLIC_URL    = 'https://images.danarecipes.com';
const R2_ENDPOINT      = `https://${R2_ACCOUNT_ID}.r2.cloudflarestorage.com`;
const R2_ACCESS_KEY    = import.meta.env?.VITE_R2_ACCESS_KEY_ID    || '3e5bcf4486fa524f98a6ee917448395a';
const R2_SECRET_KEY    = import.meta.env?.VITE_R2_SECRET_ACCESS_KEY || 'c130f5886285c445ce5681a60900d79e9b0c672557ff04a5e8ab77c02c67f2b2';
const STORAGE_FOLDERS  = {
  article: 'articles',
  pin:     'pins'
};
// Normalize legacy folder names used in older callers (e.g. AddBlogPost.jsx)
const FOLDER_ALIASES = { article_images: 'articles', pin_images: 'pins' };

// ─── AWS SIGNATURE V4 HELPERS (browser-compatible) ───────────────────────────
async function _sha256hex(data) {
  const buf = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}
async function _hmac(key, msg) {
  const k = key instanceof ArrayBuffer ? new Uint8Array(key) : (typeof key === 'string' ? new TextEncoder().encode(key) : key);
  const ck = await crypto.subtle.importKey('raw', k, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return crypto.subtle.sign('HMAC', ck, new TextEncoder().encode(msg));
}
function _hex(buf) {
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Upload a Uint8Array directly to Cloudflare R2 using AWS Signature V4.
 * Returns the public R2 URL.
 */
async function _uploadToR2(fileBytes, key, contentType) {
  const host = `${R2_ACCOUNT_ID}.r2.cloudflarestorage.com`;
  const url  = `${R2_ENDPOINT}/${R2_BUCKET}/${key}`;
  const region = 'auto';

  const now      = new Date();
  const amzDate  = now.toISOString().replace(/[-:]/g, '').slice(0, 15) + 'Z';
  const datestamp = amzDate.slice(0, 8);

  const payloadHash   = await _sha256hex(fileBytes);
  const signedHeaders = 'content-type;host;x-amz-content-sha256;x-amz-date';
  const canonicalHeaders =
    `content-type:${contentType}\n` +
    `host:${host}\n` +
    `x-amz-content-sha256:${payloadHash}\n` +
    `x-amz-date:${amzDate}\n`;

  const canonicalRequest = ['PUT', `/${R2_BUCKET}/${key}`, '', canonicalHeaders, signedHeaders, payloadHash].join('\n');
  const credentialScope  = `${datestamp}/${region}/s3/aws4_request`;
  const stringToSign     = ['AWS4-HMAC-SHA256', amzDate, credentialScope, await _sha256hex(new TextEncoder().encode(canonicalRequest))].join('\n');

  const kDate    = await _hmac(`AWS4${R2_SECRET_KEY}`, datestamp);
  const kRegion  = await _hmac(kDate, region);
  const kService = await _hmac(kRegion, 's3');
  const kSigning = await _hmac(kService, 'aws4_request');
  const signature = _hex(await _hmac(kSigning, stringToSign));

  const authorization = `AWS4-HMAC-SHA256 Credential=${R2_ACCESS_KEY}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

  const r2Res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization':       authorization,
      'Content-Type':        contentType,
      'x-amz-content-sha256': payloadHash,
      'x-amz-date':          amzDate,
      'Cache-Control':       'public, max-age=31536000, immutable',
    },
    body: fileBytes,
  });

  if (!r2Res.ok) {
    const errText = await r2Res.text().catch(() => '');
    throw new Error(`R2 upload failed (${r2Res.status}): ${errText}`);
  }

  const finalUrl = `${R2_PUBLIC_URL}/${key}`;
  return finalUrl;
}

// Discord CDN hostname patterns — any URL from these must be downloaded & re-stored
const DISCORD_HOSTS = [
  'cdn.discordapp.com',
  'media.discordapp.net',
  'images-ext-1.discordapp.net',
  'images-ext-2.discordapp.net',
];

/**
 * Returns true when a URL points to a Discord CDN host.
 */
export function isDiscordUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const host = new URL(url).hostname;
    return DISCORD_HOSTS.some((h) => host === h || host.endsWith('.' + h));
  } catch {
    return false;
  }
}

/**
 * Load image helper function
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Convert image to WebP format
 */
export async function convertToWebP(imageFile, quality = 0.92, targetSize = null) {
  try {
    validateImageFile(imageFile);

    // Step 1: Humanize metadata (strip AI metadata, add camera EXIF)
    const humanizedFile = await humanizeImageMetadata(imageFile, 'food photography');

    const img = await loadImage(humanizedFile);
    const canvas = document.createElement('canvas');

    // Preserve original aspect ratio from Discord/MidJourney — no crop, no resize,
    // unless a targetSize was requested for a square generated image.
    const isSquare = img?.width === img?.height;
    const resizeTo = (targetSize && isSquare) ? targetSize : null;
    canvas.width = resizeTo || img?.width;
    canvas.height = resizeTo || img?.height;
    const ctx = canvas?.getContext('2d');

    if (resizeTo) {
      ctx?.drawImage(img, 0, 0, resizeTo, resizeTo);
    } else {
      ctx?.drawImage(img, 0, 0);
    }
    // Convert to WebP with compression
    const webpBlob = await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/webp', quality);
    });
    const webpFile = new File(
      [webpBlob],
      imageFile.name.replace(/\.[^.]+$/, '.webp'),
      { type: 'image/webp' }
    );
    
    if (humanizedFile?.exifMetadata) {
      webpFile.exifMetadata = humanizedFile?.exifMetadata;
    }
    console.log(`Image converted: ${(imageFile?.size / 1024)?.toFixed(2)}KB -> ${(webpFile?.size / 1024)?.toFixed(2)}KB (${img?.width}x${img?.height})`);
    return webpFile;
  } catch (error) {
    console.error('Error converting to WebP:', error);
    throw new Error(`Failed to convert image: ${error.message}`);
  }
}

/**
 * Convert ArrayBuffer to WebP format
 * Used for processing images downloaded from URLs
 */
export async function convertToWebPFromBuffer(arrayBuffer, focusKeyword = 'food photography', quality = 0.92) {
  try {
    // Convert ArrayBuffer to Blob
    const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
    
    // Convert Blob to File
    const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
    
    // Use existing convertToWebP function
    return await convertToWebP(file, quality);
  } catch (error) {
    console.error('Error converting buffer to WebP:', error);
    throw new Error(`Failed to convert buffer to WebP: ${error.message}`);
  }
}

/**
 * Upload image file to GymPlix storage.
 * Always enforces a .webp extension on the stored filename.
 * Returns the permanent public URL:
 *   https://danarecipes.com/app/storage/article_images/<filename>.webp
 *
 * @param {File} file - Image file to upload (must already be converted to WebP)
 * @param {Object} options - Upload options
 * @param {string} options.folder - Target folder ('article_images' or 'pin_images')
 * @returns {Promise<string>} - Permanent public URL of uploaded image
 */
export async function uploadToStorage(file, options = {}) {
  try {
    if (!file) {
      throw new Error('No file provided for upload');
    }
    // Validate file
    validateImageFile(file);
    // Normalize legacy folder names (article_images → articles, pin_images → pins)
    const rawFolder = options?.folder || STORAGE_FOLDERS.article;
    const folder    = FOLDER_ALIASES[rawFolder] || rawFolder;
    if (!folder) {
      throw new Error('Upload folder is required');
    }
    // Generate unique filename — always .webp regardless of original extension
    const timestamp    = Date.now();
    const randomString = Math.random()?.toString(36)?.substring(2, 15);
    // Strip any existing extension then force .webp
    const baseName = (file?.name || 'image').replace(/\.[^.]+$/, '');
    const fileName = `${timestamp}-${randomString}-${baseName}.webp`;
    const key      = `${folder}/${fileName}`;

    // Upload directly to Cloudflare R2 using AWS Signature V4
    const fileBytes   = new Uint8Array(await file.arrayBuffer());
    const permanentUrl = await _uploadToR2(fileBytes, key, 'image/webp');

    console.log(`Image uploaded successfully: ${permanentUrl}`);
    return permanentUrl;
  } catch (error) {
    console.error('Error uploading to storage:', error);
    throw new Error(`Failed to upload image: ${error.message}`);
  }
}

/**
 * Download an image from any remote URL (including Discord CDN),
 * convert it to WebP, upload to Hostinger storage, and return the
 * permanent danarecipes.com URL.
 *
 * Safe to call for non-Discord URLs too — it normalises all 4 body
 * images through the same pipeline so no external URL is ever saved.
 *
 * @param {string} remoteUrl - Source image URL (Discord CDN or any remote)
 * @param {string} location  - Image slot: 'main' | 'top' | 'middle' | 'end'
 * @returns {Promise<string>} Permanent storage URL
 */
export async function downloadAndStoreFromUrl(remoteUrl, location = 'image') {
  if (!remoteUrl || typeof remoteUrl !== 'string') {
    throw new Error('Invalid remote URL provided');
  }
  // Download the remote image
  const response = await fetch(remoteUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch remote image: ${response.status} ${remoteUrl}`);
  }
  const arrayBuffer = await response.arrayBuffer();

  // Detect MIME from Content-Type header; fall back to jpeg
  const contentType = response.headers.get('content-type') || 'image/jpeg';
  const mimeType = contentType.split(';')[0].trim() || 'image/jpeg';

  // Wrap in a File so convertToWebP can process it
  const blob = new Blob([arrayBuffer], { type: mimeType });
  const tempFile = new File([blob], `${location}.jpg`, { type: mimeType });

  // Convert to WebP (resize square generated images to 724x724, strip metadata)
  const webpFile = await convertToWebP(tempFile, 1, 724);

  // Upload to Hostinger and get permanent URL
  const permanentUrl = await uploadToStorage(webpFile, { folder: STORAGE_FOLDERS.article });
  return permanentUrl;
}

/**
 * NEW: Remove all AI metadata and add realistic camera-style EXIF metadata
 */
export async function humanizeImageMetadata(imageFile, focusKeyword) {
  try {
    const img = await loadImage(imageFile);
    const canvas = document.createElement('canvas');
    canvas.width = img?.width;
    canvas.height = img?.height;
    const ctx = canvas?.getContext('2d');
    ctx?.drawImage(img, 0, 0);
    
    // Strip existing metadata via canvas redraw — use lossless PNG so no quality
    // is lost before the final WebP encode step in convertToWebP.
    const strippedBlob = await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });

    // Generate realistic EXIF metadata
    const exifData = await openaiService?.generateRealisticExifMetadata(focusKeyword);

    if (!exifData) {
      console.warn('Failed to generate EXIF metadata, returning stripped image');
      return new File([strippedBlob], imageFile.name, { type: 'image/png' });
    }

    console.log('Generated realistic EXIF metadata:', exifData);

    const humanizedFile = new File([strippedBlob], imageFile.name, {
      type: 'image/png',
      lastModified: new Date(exifData.DateTimeOriginal).getTime()
    });
    
    // Attach metadata for server-side processing
    humanizedFile.exifMetadata = exifData;
    
    return humanizedFile;
  } catch (error) {
    console.error('Error humanizing image metadata:', error);
    return imageFile;
  }
}

/**
 * Create object URL for preview
 */
export function createImagePreview(file) {
  return URL.createObjectURL(file);
}

/**
 * Revoke object URL to free memory
 */
export function revokeImagePreview(url) {
  URL.revokeObjectURL(url);
}

/**
 * Validate image file
 */
export function validateImageFile(file) {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (!validTypes?.includes(file?.type)) {
    throw new Error('Invalid file type. Please upload JPG, PNG, or WebP images.');
  }
  if (file?.size > maxSize) {
    throw new Error('File size exceeds 10MB limit.');
  }
  return true;
}

/**
 * Process multiple images for upload
 */
export async function processImages(imageFiles) {
  const processedImages = [];
  for (const imageData of imageFiles) {
    if (!imageData?.file) continue;
    try {
      validateImageFile(imageData?.file);
      const webpFile = await convertToWebP(imageData?.file);
      
      processedImages?.push({
        location: imageData?.location,
        file: webpFile,
        originalName: imageData?.file?.name,
        preview: createImagePreview(webpFile),
      });
    } catch (error) {
      console.error(`Error processing image for ${imageData?.location}:`, error);
      throw error;
    }
  }
  return processedImages;
}

export default {
  convertToWebP,
  convertToWebPFromBuffer,
  uploadToStorage,
  downloadAndStoreFromUrl,
  isDiscordUrl,
  createImagePreview,
  revokeImagePreview,
  validateImageFile,
  processImages,
  humanizeImageMetadata
};
